import React, { useEffect, useState } from 'react';
import { Play, BookOpen, Heart } from 'lucide-react';
import { api, type QuizSummary } from '../lib/api';
import { FavoriteButton } from './FavoriteButton';
import { ShareButton } from './ShareButton';

interface Props {
  /** Las marcas vigentes: quitar una aquí la saca de la lista al momento. */
  favoriteIds: Set<string>;
  onToggleFavorite: (quizId: string) => void;
  onHost: (quiz: QuizSummary) => void;
  onStudy: (quizId: string) => void;
}

/** Las trivias que quien mira su perfil ha marcado con el corazón. */
export const FavoriteQuizzes: React.FC<Props> = ({ favoriteIds, onToggleFavorite, onHost, onStudy }) => {
  const [quizzes, setQuizzes] = useState<QuizSummary[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ quizzes: QuizSummary[] }>('/quizzes/favorites');
        setQuizzes(data.quizzes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar tus favoritas.');
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  // Se filtra por las marcas vigentes en vez de recargar: desmarcar una la
  // quita en el acto, y si el servidor lo rechaza vuelve a aparecer sola.
  const visibles = quizzes.filter((q) => favoriteIds.has(q.id));

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-display font-bold">Favoritas</h2>
        {!cargando && visibles.length > 0 && <span className="text-sm text-ink-500">{visibles.length}</span>}
      </div>

      {error && <p className="text-danger-400 text-sm">{error}</p>}

      {cargando ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="surface h-28 shimmer" />
          ))}
        </div>
      ) : visibles.length === 0 ? (
        <div className="surface p-8 text-center space-y-2">
          <Heart className="w-8 h-8 mx-auto text-ink-600" />
          <p className="text-ink-300 text-sm">
            Aún no tienes favoritas. Pulsa el corazón de cualquier trivia del catálogo para guardarla aquí.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {visibles.map((quiz) => (
            <article
              key={quiz.id}
              className="surface p-4 flex flex-col gap-3 hover:border-ink-600 transition-colors"
            >
              <div className="flex items-start gap-3">
                <span className="font-emoji text-3xl leading-none shrink-0" aria-hidden="true">
                  {quiz.coverEmoji}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-ink-50 leading-snug line-clamp-2">{quiz.title}</h3>
                  <p className="text-ink-500 text-xs mt-1">
                    {quiz.creatorName} · {quiz.questionCount}{' '}
                    {quiz.questionCount === 1 ? 'pregunta' : 'preguntas'}
                  </p>
                </div>
                <FavoriteButton active title={quiz.title} onToggle={() => onToggleFavorite(quiz.id)} />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onHost(quiz)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm
                             bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
                >
                  <Play className="w-3.5 h-3.5" fill="currentColor" />
                  Anfitrión
                </button>
                <button
                  onClick={() => onStudy(quiz.id)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-semibold text-sm
                             bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200 transition-colors"
                  title="Practicar a tu ritmo, sin sala"
                >
                  <BookOpen className="w-4 h-4" />
                  Estudiar
                </button>
                <ShareButton quiz={quiz} />
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
